/**
 * The composer's state and what it does, for every place Maya writes from: the words, one file from Assets or a
 * recorded note, the + menu's list, Draft with AI, and the send. The place decides where a send goes; the composer
 * keeps what was written until it has gone.
 */
import { type KeyboardEvent as ReactKeyEvent, useCallback, useEffect, useRef, useState } from "react";
import type { Attachment, NoteKind } from "../../shared/types";
import { openAssets } from "../assets/store";
import { attachmentOf, type FitName, lenLabel } from "../assets/library";
import type { MenuItem } from "../components/ui/Menu";
import { useToast } from "../components/ui/Toast";
import { recordNote } from "../lib/noteUpload";
import { type Take, useNoteRecorder } from "./useNoteRecorder";

/** What waits in the composer to go with the words. */
export type Pending =
  | { type: "file"; att: Attachment; thumb: string | null }
  | { type: "note"; kind: NoteKind; seconds: number; preview: string; state: "uploading" | "processing" | "ready" | "failed"; pct: number; att: Attachment | null };

export interface Outgoing {
  text: string;
  subject: string;
  attachment: Attachment | null;
}

const PICKS: Record<"media" | "file" | "assets", [FitName, string]> = {
  media: ["media", "Send a photo or video"],
  file: ["docs", "Send a file"],
  assets: ["send", "Send from Assets"],
};

export function useComposer({ to, away, email = false, subject: startSubject = "", draft, ask, onSend }: {
  /** Who the words go to, for the picker's title: "everyone in the room". */
  to: string;
  /** A question is open over the place, which leaves the recorder. */
  away: boolean;
  /** An email has no notes. */
  email?: boolean;
  /** The subject an email opens with. */
  subject?: string;
  /** Draft with AI: the words of a reply to edit; leave out where there is no one to draft for. */
  draft?: () => Promise<string>;
  /** Where a question with choices is asked from the + menu, for a place with no button of its own. */
  ask?: () => void;
  /** Sends it; a rejection keeps everything that was written. */
  onSend: (out: Outgoing) => Promise<void>;
}) {
  const toast = useToast();
  const [text, setText] = useState("");
  const [subject, setSubject] = useState(startSubject);
  const [pending, setPending] = useState<Pending | null>(null);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const box = useRef<HTMLTextAreaElement>(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const upload = useRef<AbortController | null>(null);

  useEffect(() => setSubject(startSubject), [startSubject]);
  useEffect(() => () => upload.current?.abort(), []);

  const grow = useCallback(() => {
    const el = box.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, []);

  const dropPending = useCallback(() => {
    upload.current?.abort();
    upload.current = null;
    setPending((p) => {
      if (p?.type === "note") URL.revokeObjectURL(p.preview);
      return null;
    });
  }, []);

  const sendTake = useCallback(
    (take: Take) => {
      dropPending();
      const ctl = new AbortController();
      upload.current = ctl;
      setPending({ type: "note", kind: take.kind, seconds: take.seconds, preview: take.preview, state: "uploading", pct: 0, att: null });
      const patch = (next: Partial<Extract<Pending, { type: "note" }>>) =>
        setPending((p) => (p?.type === "note" && p.preview === take.preview ? { ...p, ...next } : p));
      recordNote(take.kind, take.blob, {
        name: take.kind === "voice" ? "Voice note" : "Video note",
        signal: ctl.signal,
        live: take.live,
        onProgress: (pct) => patch({ pct }),
        onPhase: (phase) => patch({ state: phase }),
      })
        .then((note) => {
          if (ctl.signal.aborted) return;
          patch({ state: "ready", att: { ...note.attachment, duration: note.attachment.duration ?? lenLabel(take.seconds) } });
        })
        .catch(() => {
          if (!ctl.signal.aborted) patch({ state: "failed" });
        });
    },
    [dropPending],
  );

  const recorder = useNoteRecorder(sendTake, (m) => toast({ text: m, icon: "info" }));
  const { cancel: stopRecording, rec: recording } = recorder;

  // Leaving the recorder stops it and frees the camera: the Email side, a question, or Esc while it records.
  useEffect(() => {
    stopRecording();
    setVideoOpen(false);
  }, [email, stopRecording]);
  useEffect(() => {
    if (!away) return;
    stopRecording();
    setVideoOpen(false);
  }, [away, stopRecording]);
  useEffect(() => {
    if (!recording) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") stopRecording();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [recording, stopRecording]);

  const pick = (k: keyof typeof PICKS) => {
    const [fits, title] = PICKS[k];
    openAssets({
      fits,
      title: `${title} to ${to}`,
      cta: "Attach it",
      onPick: (asset) => {
        const att = attachmentOf(asset);
        if (!att) {
          toast({ text: "That one is still getting ready. Try it again in a moment.", icon: "info" });
          return;
        }
        dropPending();
        setPending({ type: "file", att, thumb: asset.kind === "video" ? asset.poster_url : null });
        box.current?.focus();
      },
    });
  };

  const draftWithAi = () => {
    if (drafting || !draft) return;
    setDrafting(true);
    draft()
      .then((words) => {
        setText(words);
        requestAnimationFrame(() => {
          grow();
          box.current?.focus();
        });
      })
      .catch(() => toast({ text: "We couldn't draft a reply just now. Try again.", icon: "info" }))
      .finally(() => setDrafting(false));
  };

  const send = async () => {
    if (sending) return;
    const words = text.trim();
    if (pending?.type === "note" && pending.state !== "ready") {
      toast({ text: pending.state === "failed" ? "The note didn't upload. Record it again." : "One moment, the note is still uploading.", icon: "info" });
      return;
    }
    const attachment = pending?.att ?? null;
    if (!words && !attachment) {
      box.current?.focus();
      toast({ text: "Write something first", icon: "info" });
      return;
    }
    setSending(true);
    try {
      await onSend({ text: words, subject: subject.trim(), attachment });
      setText("");
      setPending((p) => {
        if (p?.type === "note") URL.revokeObjectURL(p.preview);
        return null;
      });
      upload.current = null;
      requestAnimationFrame(grow);
    } catch {
      // Why: the thread said why; everything written stays for another try.
    } finally {
      setSending(false);
    }
  };

  /** A file from Assets leaves the recorder first; a question leaves it through `away`. */
  const leave = (then: () => void) => () => {
    stopRecording();
    then();
  };
  const items: Array<MenuItem | "sep"> = [
    { label: "Photo or video", sub: "", icon: "pic", onSelect: leave(() => pick("media")) },
    { label: "File", sub: "PDF or document", icon: "file", onSelect: leave(() => pick("file")) },
    { label: "From Assets", sub: "", icon: "fold", onSelect: leave(() => pick("assets")) },
  ];
  if (!email) {
    items.push(
      "sep",
      { label: "Record a voice note", sub: "", icon: "mic", onSelect: () => void recorder.start() },
      { label: "Record a video note", sub: "Up to 10 minutes", icon: "video", onSelect: () => setVideoOpen(true) },
    );
  }
  if (ask && !email) items.push("sep", { label: "Ask a question with choices", sub: "2 to 4 answers", icon: "list", onSelect: ask });
  if (draft) items.push("sep", { label: "Draft with AI", sub: "A reply to edit", icon: "sparkle", onSelect: draftWithAi });

  // Why Enter sends: on a keyboard it does, except in an email (Ctrl or Cmd Enter) and on a touch screen (a new line).
  const onKeyDown = (e: ReactKeyEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey) return;
    const mod = e.metaKey || e.ctrlKey;
    if (email ? mod : !matchMedia("(hover: none)").matches || mod) {
      e.preventDefault();
      void send();
    }
  };

  return { text, setText, onKeyDown, subject, setSubject, pending, sending, drafting, box, grow, recorder, videoOpen, setVideoOpen, dropPending, sendTake, draftWithAi, send, items };
}
